import { useState, useEffect } from "react";
import { 
  Bell, 
  X, 
  CheckCircle, 
  AlertTriangle, 
  Info, 
  FileSignature, 
  DollarSign,
  Clock,
  TrendingUp,
  Users,
  ArrowRight
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// Alert types with styling
const ALERT_TYPES = {
  success: { icon: CheckCircle, color: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  warning: { icon: AlertTriangle, color: 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20' },
  contract: { icon: FileSignature, color: 'text-purple-400', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  capital: { icon: DollarSign, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10', border: 'border-[#D4AF37]/20' },
  velocity: { icon: Clock, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  partner: { icon: Users, color: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
};

export default function AlertsPanel({ isOpen, onClose, deals = [], contracts = [] }) {
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  // Generate alerts based on deal and contract status
  useEffect(() => {
    const generatedAlerts = [];

    // Check deals for alerts
    deals.forEach(deal => {
      // Support both field naming conventions
      const daysInStage = deal.daysInStage || deal.days_in_stage || 0;
      const address = deal.property_address || deal.address || 'Property';
      const stage = deal.executionStage || deal.pipeline_stage || deal.status || 'unknown';
      const capitalRequired = deal.capitalRequired || deal.purchase_price || 0;
      
      // Velocity warnings - deals stuck in stage
      if (daysInStage > 14) {
        generatedAlerts.push({
          id: `velocity_${deal.deal_id}`,
          type: 'velocity',
          title: 'Velocity Warning',
          message: `${address.split(',')[0]} has been in ${stage.replace(/_/g, ' ')} for ${daysInStage} days`,
          action: { label: 'View Deal', path: `/deals/${deal.deal_id}` },
          timestamp: new Date(deal.updated_at || Date.now()),
          priority: 'high'
        });
      }

      // High-value deals requiring attention
      if (capitalRequired > 500000 && ['contract', 'contracted', 'under_contract'].includes(stage)) {
        generatedAlerts.push({
          id: `capital_${deal.deal_id}`,
          type: 'capital',
          title: 'High-Value Transaction',
          message: `$${(capitalRequired / 1000).toFixed(0)}K deal in contract - ${address.split(',')[0]}`,
          action: { label: 'Review', path: `/deals/${deal.deal_id}` },
          timestamp: new Date(deal.updated_at || Date.now()),
          priority: 'medium'
        });
      }
      
      // Closing soon alerts
      if (['closing', 'ready_to_close'].includes(stage)) {
        generatedAlerts.push({
          id: `closing_${deal.deal_id}`,
          type: 'success',
          title: 'Closing Soon',
          message: `${address.split(',')[0]} is ready for closing`,
          action: { label: 'View Deal', path: `/deals/${deal.deal_id}` },
          timestamp: new Date(deal.updated_at || Date.now()),
          priority: 'medium'
        });
      }
      
      // High risk alerts
      if (deal.riskScore && deal.riskScore > 60) {
        generatedAlerts.push({
          id: `risk_${deal.deal_id}`,
          type: 'warning',
          title: 'High Risk Exposure',
          message: `${address.split(',')[0]} has a risk score of ${deal.riskScore}`,
          action: { label: 'Review', path: `/deals/${deal.deal_id}` },
          timestamp: new Date(deal.updated_at || Date.now()),
          priority: 'medium'
        });
      }
    });

    // Check contracts for signatures
    contracts.forEach(contract => {
      if (contract.status === 'awaiting_signatures') {
        const pendingSigs = contract.signatures?.filter(s => s.status === 'pending').length || 0;
        if (pendingSigs > 0) {
          generatedAlerts.push({
            id: `contract_${contract.contract_id}`,
            type: 'contract',
            title: 'Signatures Pending',
            message: `${contract.name} awaiting ${pendingSigs} signature${pendingSigs > 1 ? 's' : ''}`,
            action: { label: 'View Contract', path: '/contracts' },
            timestamp: new Date(contract.updated_at || Date.now()),
            priority: 'high'
          });
        }
      }
    });

    // Sort by priority and timestamp
    generatedAlerts.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

    setAlerts(generatedAlerts.slice(0, 10));
  }, [deals, contracts]);

  const dismissAlert = (alertId) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const handleAction = (action) => {
    if (action?.path) {
      navigate(action.path);
      onClose();
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return new Date(date).toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 right-0 h-full w-96 bg-[#0A0A0A]/80 backdrop-blur-sm border-l border-white/[0.06] shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#D4AF37]" />
          <h2 className="text-white font-semibold">Alerts</h2>
          {alerts.length > 0 && (
            <span className="px-2 py-0.5 text-[10px] font-mono bg-[#D4AF37]/20 text-[#D4AF37] rounded">
              {alerts.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-white/[0.02] rounded transition-all duration-300"
        >
          <X className="w-5 h-5 text-white/50" />
        </button>
      </div>

      {/* Alerts List */}
      <div className="flex-1 overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <Bell className="w-12 h-12 text-white/30 mb-4" />
            <h3 className="text-white font-medium mb-1">All Clear</h3>
            <p className="text-sm text-white/40">No alerts at this time. Your transactions are on track.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.02]">
            {alerts.map((alert) => {
              const alertConfig = ALERT_TYPES[alert.type] || ALERT_TYPES.info;
              const Icon = alertConfig.icon;
              
              return (
                <div
                  key={alert.id}
                  onClick={() => alert.action && handleAction(alert.action)}
                  className={`p-4 ${alertConfig.bg} ${alertConfig.border} border-l-2 transition-all duration-200 ${
                    alert.action ? 'cursor-pointer hover:bg-white/[0.05]' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${alertConfig.bg}`}>
                      <Icon className={`w-4 h-4 ${alertConfig.color}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className={`text-sm font-medium ${alertConfig.color}`}>{alert.title}</h4>
                        <button
                          onClick={(e) => { e.stopPropagation(); dismissAlert(alert.id); }}
                          className="p-1 hover:bg-[#1D222B] rounded opacity-50 hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3 text-white/40" />
                        </button>
                      </div>
                      
                      <p className="text-sm text-white/70 mb-2">{alert.message}</p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-white/40 font-mono">
                          {formatTime(alert.timestamp)}
                        </span>
                        
                        {alert.action && (
                          <span className={`flex items-center gap-1 text-xs ${alertConfig.color}`}>
                            {alert.action.label}
                            <ArrowRight className="w-3 h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/[0.06] bg-black/20">
        <button
          onClick={() => setAlerts([])}
          className="w-full py-2 text-sm text-white/50 hover:text-white border border-white/[0.06] rounded hover:bg-white/[0.02] transition-all"
        >
          Clear All Alerts
        </button>
      </div>
    </div>
  );
}
